import { useAuth0 } from "@auth0/auth0-react";
import { useMsal } from "@azure/msal-react";
import { jwtDecode } from "jwt-decode";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { HttpService } from "./services/httpService";

export const useAuth = () => {
  const { instance } = useMsal();
  const { loginWithRedirect } = useAuth0();

  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || "";
  const APP_URL = process.env.REACT_APP_APP_URL || "";
  const DEV_APP_URL = process.env.REACT_APP_DEV_APP_URL || "";
  const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
  const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
  const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";
  const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE || "";

  const apiBaseUrl =
    window.location.hostname === "localhost"
      ? `${API_URL}/api/auth/validate`
      : window.location.hostname === new URL(PROD_APP_URL).hostname
        ? `${PROD_API_URL}/api/auth/validate`
        : `${DEV_API_URL}/api/auth/validate`;

  const redirectUrl =
    window.location.hostname === "localhost"
      ? `${APP_URL}/`
      : window.location.hostname === new URL(PROD_APP_URL).hostname
        ? `${PROD_APP_URL}/`
        : `${DEV_APP_URL}/`;

  const loginWithMsal = async (email: string, portal: string) => {
    const attemptID = crypto.randomUUID();

    try {
      const loginResponse = await instance.loginPopup({
        scopes: ["user.read"],
        redirectUri: redirectUrl,
        loginHint: email,
        prompt: "login",
      });
      const accessToken = loginResponse.idToken;

      const response = await fetch(apiBaseUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ accessToken, portal }),
      });

      const validationResponse = await response.json();

      return validationResponse.message === "User validated successfully."
        ? [accessToken, ""]
        : [null, validationResponse.message];
    } catch (error: any) {
      console.error("Login failed:", error);
      await HttpService.logFrontendError(error, {
        flow: "msal",
        stage: "loginPopup",
        email,
        portal,
        attemptID,
      });
      return [null, "Login popup failed or was closed. Please try again."];
    }
  };

  const loginWithAuth0 = async (
    email: string,
    portal: string,
  ): Promise<[string | null, string]> => {
    const attemptID = crypto.randomUUID();

    localStorage.setItem("pendingPortal", portal);
    localStorage.setItem("pendingEmail", email);
    localStorage.setItem("pendingAttemptID", attemptID);

    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: "email",
          login_hint: email,
          audience: AUTH0_AUDIENCE,
          scope: "openid profile email",
          prompt: "login",
          max_age: 0,
          redirect_uri: `${redirectUrl}auth/callback`,
        },
        appState: { returnTo: `/${portal}` },
      });

      return [null, "Redirecting to login..."];
    } catch (err: any) {
      await HttpService.logFrontendError(err, {
        flow: "auth0",
        stage: "loginWithRedirect",
        email,
        portal,
        attemptID,
      });

      return [null, "Could not start login. Please try again."];
    }
  };

  const logout = async () => {
    try {
      localStorage.clear();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const decodeToken = useCallback((token: string) => {
    const decodedToken: any = jwtDecode(token);
    const issuer = decodedToken.iss || "";
    const isMicrosoftToken = issuer.includes("login.microsoftonline.com");
    if (!isMicrosoftToken) {
      const decodedToken: any = jwtDecode(token);
      return decodedToken.email;
    } else {
      const decodedToken: any = jwtDecode(token);
      return decodedToken.preferred_username;
    }
  }, []);

  return { loginWithMsal, loginWithAuth0, logout, decodeToken };
};
